import { useEffect, useRef, useState } from 'react'
import NextImage from 'next/image'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Spinner } from '@wartek-id/spinner'

import type { PerencanaanPS } from 'types/kinerja-tim-penilai-ps'

const consentScript = 'SAYA SETUJUI PERENCANAAN SEKALIGUS'

export default function AgreeButton({
  perencanaan,
  onConfirm,
  onSuccess,
  onError,
}: {
  perencanaan: PerencanaanPS[]
  onConfirm: () => Promise<void>
  onSuccess: () => void
  onError: (error: any) => void
}) {
  const [activeDialog, setActiveDialog] = useState<
    'CONFIRMATION' | 'LOADING' | 'SUCCESS' | null
  >(null)
  const total = perencanaan.length
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [agreementText, setAgreementText] = useState('')
  const [isValid, setIsValid] = useState(true)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (activeDialog === 'CONFIRMATION') {
      setAgreementText('')
      setIsDirty(false)
    }
  }, [activeDialog])

  const handleConfirm = async () => {
    if (agreementText === consentScript) {
      setIsValid(true)
      setActiveDialog('LOADING')

      try {
        await onConfirm?.()
        setTimeout(() => {
          setActiveDialog('SUCCESS')
        }, 1000)
      } catch (error) {
        console.error(error)
        setActiveDialog(null)
        onError(error)
      }
    } else {
      setIsValid(false)

      textareaRef.current?.focus()
    }
  }

  const handleSuccessConfirmation = () => {
    setActiveDialog(null)
    onSuccess()
  }

  return (
    <>
      <Button size="lg" onClick={() => setActiveDialog('CONFIRMATION')}>
        <div className="flex gap-2">
          <Icon color="inverse">check_outline</Icon>
          Setujui ({total})
        </div>
      </Button>

      <Dialog
        size="sm"
        isOpen={activeDialog !== null}
        onClose={() => setActiveDialog(null)}
      >
        <DialogOverlay />
        <DialogContent>
          {activeDialog === 'CONFIRMATION' && (
            <div className="p-6" data-testid="confirmation">
              <div className="flex flex-col gap-4">
                <div>
                  <NextImage
                    src={'/illustrations/img-collect-files-folder.png'}
                    alt=""
                    height={84}
                    width={84}
                  />
                </div>
                <Text variant="display-sm">
                  Konfirmasi untuk Setujui Sekaligus
                </Text>
                <Text variant="body-lg" color="subdued">
                  <strong>Perencanaan tidak dapat diubah lagi.</strong> Semua
                  data Perencanaan akan otomatis dikirim ke E-Kinerja dan
                  Pengawas Sekolah dapat langsung mulai Pelaksanaan.
                </Text>
                <Text variant="body-lg" color="subdued">
                  Mohon ketik kalimat persetujuan di bawah ini:
                </Text>
                <div className="bg-blue-5 p-1 rounded">
                  <Text
                    variant="heading-sm"
                    color="informational"
                    className="select-none !text-blue-60"
                  >
                    {consentScript}
                  </Text>
                </div>
                <div className="w-80">
                  <Text className="mb-1">Kalimat persetujuan</Text>

                  <Textarea
                    ref={textareaRef}
                    onChange={(e) => {
                      if (!isDirty) {
                        setIsDirty(true)
                      }

                      setAgreementText(e.target.value.toUpperCase())
                    }}
                    rows={2}
                    isInvalid={!isValid}
                    value={agreementText}
                    errorMessage={
                      isValid ? '' : 'Mohon cek ejaan dan coba lagi.'
                    }
                  ></Textarea>
                </div>
              </div>

              <div className="flex gap-5 mt-7 w-80">
                <Button
                  onClick={() => setActiveDialog(null)}
                  color="white"
                  size="lg"
                  className="flex-grow"
                >
                  Kembali
                </Button>
                <Button
                  color="blue"
                  size="lg"
                  onClick={handleConfirm}
                  className="flex-grow"
                >
                  Konfirmasi
                </Button>
              </div>
            </div>
          )}
          {activeDialog === 'LOADING' && (
            <div className="px-6 py-9 flex flex-col items-center justify-center">
              <div
                className="mt-6"
                style={
                  {
                    '--lg-size': '72px',
                    '--lg-border-size': '16px',
                  } as React.CSSProperties
                }
              >
                <Spinner size="lg" />
              </div>
              <div className="text-center mt-9 w-96">
                <Text variant="display-sm">Menyetujui Perencanaan...</Text>
                <Text variant="body-lg" color="subdued">
                  Mohon tunggu sebentar, ya.
                </Text>
              </div>
            </div>
          )}
          {activeDialog === 'SUCCESS' && (
            <div className="p-6">
              <div className="flex flex-col">
                <div>
                  <NextImage
                    src={'/illustrations/img-thumbs-up.png'}
                    alt=""
                    height={84}
                    width={84}
                  />
                </div>
                <div className="mt-4">
                  <Text variant="display-sm">
                    {total} Perencanaan disetujui!
                  </Text>
                  <Text variant="body-lg" color="subdued" className="mt-2">
                    Anda dapat setujui Perencanaan lain jika ada yang belum
                    disetujui. Mohon cek secara berkala.
                  </Text>
                </div>
                <div className="mt-6" onClick={handleSuccessConfirmation}>
                  <Button size="lg">Saya mengerti</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
